import _ from "lodash";
import { useState } from "react";

import axios from "../../../../../components/api/axios";
import { useDebouncedEffect } from "../../../../../hooks/useDebounceEffect";
import useService from "../../../../../hooks/useService";
import { convertKeysToCamelCaseDeep } from "../helper/functions";

const MIN_SEARCH_LENGTH = 5;
const SUGGESTION_LIMIT = 5;

export function useSmartyAutocomplete({ address, setAddress }) {
  const addressRepository = useService("repositories/address-repository");

  const [searchVal, setSearchVal] = useState(address?.raw ?? "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [smartyError, setSmartyError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [validAddressChange, setValidAddressChange] = useState(false);
  const [validationResponse, setValidationResponse] = useState(Boolean(address?.validated));

  const handleFocus = () => setIsFocused(true);

  const handleBlur = () => {
    setTimeout(() => setIsFocused(false), 150);
  };

  const handleSearch = (value) => {
    setSearchVal(value);
    setSmartyError(false);

    if (value.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      return;
    }

    if (address) {
      setAddress(null);
    }
  };

  const handleClear = () => {
    setSearchVal("");
    setSuggestions([]);
    setSmartyError(false);
    setAddress(null);
  };

  const getSuggestions = async (query) => {
    if (!isFocused || !process.env.REACT_APP_SMARTY_API_KEY || query.length < MIN_SEARCH_LENGTH) {
      return;
    }

    setLoading(true);

    try {
      const { data } = await axios.get("https://us-autocomplete-pro.api.smarty.com/lookup", {
        params: {
          key: process.env.REACT_APP_SMARTY_API_KEY,
          search: query,
          max_results: SUGGESTION_LIMIT,
        },
        headers: { Referer: process.env.REACT_APP_SMARTY_REFERER },
      });

      const formatted = convertKeysToCamelCaseDeep(data);
      setSuggestions(formatted.suggestions ?? []);
    } catch {
      setSmartyError(true);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const buildRawAddress = (s) =>
    s.secondary
      ? `${s.streetLine} ${s.secondary}, ${s.city} ${s.state}, ${s.zipcode}`
      : `${s.streetLine}, ${s.city} ${s.state}, ${s.zipcode}`;

  const handleSelect = async (suggestion) => {
    setLoading(true);

    try {
      const raw = buildRawAddress(suggestion);
      const route = suggestion.secondary
        ? `${suggestion.streetLine} ${suggestion.secondary}`
        : suggestion.streetLine;

      setAddress({
        raw,
        components: {
          street: suggestion.streetLine,
          secondary: suggestion.secondary || "",
          city: suggestion.city,
          state: suggestion.state,
          zipcode: suggestion.zipcode,
        },
      });

      let response = await addressRepository.post({
        raw,
        route,
        city: suggestion.city,
        state: suggestion.state,
        postCode: suggestion.zipcode,
      });

      response = convertKeysToCamelCaseDeep(response);

      if (response) {
        setValidationResponse(true);
        setSmartyError(false);
        setAddress(response);
      }
    } catch {
      setSmartyError(true);
      setValidationResponse(true);
    } finally {
      setSearchVal("");
      setSuggestions([]);
      setIsFocused(false);
      setLoading(false);
    }
  };

  const manualAddressChange = (value, key) => {
    setValidAddressChange(true);

    const updated = _.cloneDeep(address ?? {});
    updated[key] = value === "" ? null : value;

    setAddress(updated);
  };

  const clearAddressField = (key) => manualAddressChange(null, key);

  const formatDisplayedAddress = (addr) =>
    addr.secondary
      ? `${addr.streetLine} ${addr.secondary}, ${addr.city} ${addr.state}, ${addr.zipcode}`
      : `${addr.streetLine}, ${addr.city} ${addr.state}, ${addr.zipcode}`;

  useDebouncedEffect(
    () => {
      if (isFocused) {
        setSmartyError(false);
        getSuggestions(searchVal);
      }
    },
    [searchVal, isFocused],
    750
  );

  return {
    searchVal,
    suggestions,
    loading,
    smartyError,
    isFocused,
    validAddressChange,
    validationResponse,
    handleSearch,
    handleClear,
    handleSelect,
    handleFocus,
    handleBlur,
    manualAddressChange,
    clearAddressField,
    formatDisplayedAddress,
  };
}
